import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import CloudOffOutlinedIcon from "@mui/icons-material/CloudOffOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import ManageSearchOutlinedIcon from "@mui/icons-material/ManageSearchOutlined";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import DesignServicesOutlinedIcon from "@mui/icons-material/DesignServicesOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import { jwtDecode } from "jwt-decode";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import {
  pagePrimaryButtonSx,
  pageFieldSx,
} from "../../styles/pageModernStyles";
import { getUserById } from "../../api/User/userApi";
import { isSkillsApprover } from "./skillFichaHelpers";
import SkillAgentBrandSourcesFields, {
  buildDefaultUrlSelection,
} from "./skillAgentBrandSourcesFields";
import {
  cancelSkillAgentTask,
  createSkillAgentTask,
  deleteSkillAgentTask,
  dispatchSkillAgentTask,
  fetchSkillAgentArtifactBlobUrl,
  getSkillAgentTaskById,
  getSkillAgentTasksMeta,
  listSkillAgentArtifacts,
  listSkillAgentTasks,
  markSkillAgentTaskDone,
  pushSkillAgentArtifactsToDrive,
  runSkillAgentTask,
  updateSkillAgentTask,
} from "../../api/Skills/skillAgentTasksApi";
import {
  createSkillAgentSearch,
  getSkillAgentSearchesMeta,
  listSkillAgentSearches,
} from "../../api/Skills/skillAgentSearchesApi";
import {
  createSkillAgentMoodboard,
  listSkillAgentMoodboards,
} from "../../api/Skills/skillAgentMoodboardsApi";
import { listSkillFichas } from "../../api/Skills/skillFichasApi";
import {
  buildAgentTasksTree,
  moodboardStatusLabel,
  searchStatusLabel,
  taskClientLabel,
  TASK_STATUS_LABELS,
  TASK_TYPE_LABELS,
  taskStatusColor,
} from "./skillAgentTaskTree";
import SkillAgentTasksHelpDialog from "./SkillAgentTasksHelpDialog";
import SkillAgentSearchDetail from "./SkillAgentSearchDetail";
import SkillAgentMoodboardDetail from "./SkillAgentMoodboardDetail";

function TreeBranch({
  node,
  depth = 0,
  expanded,
  toggle,
  selectedId,
  selectedSearchId,
  selectedMoodboardId,
  onSelectTask,
  onSelectSearch,
  onSelectMoodboard,
  onNewSearch,
  onNewMoodboard,
  onNewTask,
  statusFilter,
}) {
  const theme = useTheme();
  const open = expanded.has(node.id);
  const isSeason = node.kind === "season";
  const isSearchs = node.kind === "searchs";
  const isMoodboards = node.kind === "moodboards";
  const isDisenos = node.kind === "disenos";
  const isLibrary = isSearchs || isMoodboards || isDisenos;
  const Icon = isMoodboards
    ? DashboardCustomizeOutlinedIcon
    : isSearchs
      ? TravelExploreOutlinedIcon
      : isDisenos
        ? DesignServicesOutlinedIcon
        : isSeason
          ? CalendarMonthOutlinedIcon
          : BusinessOutlinedIcon;
  const accent = isMoodboards
    ? "#9a3412"
    : isSearchs
      ? "#0f766e"
      : isDisenos
        ? "#4338ca"
        : isSeason
          ? theme.palette.primary.main
          : "#4f46e5";

  const tasks = (node.tasks || []).filter(
    (t) => !statusFilter || t.Status === statusFilter,
  );
  const searches = node.searches || [];
  const moodboards = node.moodboards || [];
  const childCount =
    node.children?.reduce((s, c) => {
      const n = (c.disenosNode?.tasks || []).filter(
        (t) => !statusFilter || t.Status === statusFilter,
      ).length;
      const ns =
        (c.searches || []).length +
        (c.moodboards || []).length +
        (c.disenosNode ? 1 : 0) +
        (c.searchsNode ? 1 : 0) +
        (c.moodboardsNode ? 1 : 0);
      return s + n + ns;
    }, 0) ??
    (isSearchs
      ? searches.length
      : isMoodboards
        ? moodboards.length
        : isDisenos
          ? tasks.length
          : tasks.length + searches.length + moodboards.length);

  if (node.children && childCount === 0 && statusFilter) return null;
  if (isDisenos && tasks.length === 0 && statusFilter) return null;
  if (!node.children && !isLibrary && tasks.length === 0 && statusFilter)
    return null;

  const leafPad = 1.25 + (depth + 1) * 1.35;

  const leafSx = (selected, tone) => ({
    display: "flex",
    alignItems: "center",
    gap: 1,
    ml: leafPad,
    mr: 0.75,
    minHeight: 40,
    px: 1.1,
    py: 0.65,
    mb: 0.4,
    borderRadius: 1.75,
    cursor: "pointer",
    border: "1px solid",
    borderColor: selected ? alpha(tone, 0.4) : "transparent",
    bgcolor: selected ? alpha(tone, 0.1) : "transparent",
    transition: "background-color 0.12s ease, border-color 0.12s ease",
    "&:hover": {
      bgcolor: alpha(tone, selected ? 0.12 : 0.06),
    },
  });

  const statusChipSx = {
    height: 22,
    maxWidth: 108,
    borderRadius: 1.25,
    fontWeight: 700,
    fontSize: "0.68rem",
    "& .MuiChip-label": { px: 0.9 },
  };

  return (
    <Box sx={{ mb: isSeason && depth === 0 ? 0.75 : 0 }}>
      <Box
        onClick={() => toggle(node.id)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          minHeight: isLibrary ? 34 : 38,
          px: 1,
          py: 0.55,
          pl: 1 + depth * 1.35,
          mx: 0.5,
          borderRadius: 1.75,
          cursor: "pointer",
          userSelect: "none",
          bgcolor: isLibrary ? alpha(accent, 0.05) : "transparent",
          "&:hover": { bgcolor: alpha(accent, 0.08) },
        }}
      >
        <ExpandMoreIcon
          sx={{
            fontSize: 18,
            color: "text.disabled",
            flexShrink: 0,
            transform: open ? "rotate(0deg)" : "rotate(-90deg)",
            transition: "transform 0.15s",
          }}
        />
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: 1.25,
            display: "grid",
            placeItems: "center",
            bgcolor: alpha(accent, 0.12),
            color: accent,
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 15 }} />
        </Box>
        <Typography
          variant="body2"
          fontWeight={isLibrary ? 800 : 700}
          letterSpacing={isLibrary ? 0.04 : 0}
          sx={{
            flex: 1,
            minWidth: 0,
            color: isLibrary ? accent : "text.primary",
            textTransform: isLibrary ? "uppercase" : "none",
            fontSize: isLibrary ? "0.72rem" : undefined,
          }}
          noWrap
        >
          {node.label}
        </Typography>
        {isDisenos ? (
          <IconButton
            size="small"
            aria-label="Nuevo diseño"
            onClick={(e) => {
              e.stopPropagation();
              onNewTask?.(node);
            }}
            sx={{
              width: 24,
              height: 24,
              color: accent,
              bgcolor: alpha(accent, 0.1),
              flexShrink: 0,
              "&:hover": { bgcolor: alpha(accent, 0.18) },
            }}
          >
            <AddOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        ) : null}
        {isSearchs ? (
          <IconButton
            size="small"
            aria-label="Nuevo search"
            onClick={(e) => {
              e.stopPropagation();
              onNewSearch?.(node);
            }}
            sx={{
              width: 24,
              height: 24,
              color: accent,
              bgcolor: alpha(accent, 0.1),
              flexShrink: 0,
              "&:hover": { bgcolor: alpha(accent, 0.18) },
            }}
          >
            <AddOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        ) : null}
        {isMoodboards ? (
          <IconButton
            size="small"
            aria-label="Nuevo moodboard"
            onClick={(e) => {
              e.stopPropagation();
              onNewMoodboard?.(node);
            }}
            sx={{
              width: 24,
              height: 24,
              color: accent,
              bgcolor: alpha(accent, 0.1),
              flexShrink: 0,
              "&:hover": { bgcolor: alpha(accent, 0.18) },
            }}
          >
            <AddOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        ) : null}
        <Box
          sx={{
            minWidth: 22,
            height: 22,
            px: 0.75,
            borderRadius: 999,
            display: "grid",
            placeItems: "center",
            fontSize: "0.7rem",
            fontWeight: 800,
            bgcolor: alpha(accent, 0.12),
            color: accent,
            flexShrink: 0,
          }}
        >
          {childCount || node.count || 0}
        </Box>
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        {node.children?.map((child) => (
          <TreeBranch
            key={child.id}
            node={child}
            depth={depth + 1}
            expanded={expanded}
            toggle={toggle}
            selectedId={selectedId}
            selectedSearchId={selectedSearchId}
            selectedMoodboardId={selectedMoodboardId}
            onSelectTask={onSelectTask}
            onSelectSearch={onSelectSearch}
            onSelectMoodboard={onSelectMoodboard}
            onNewSearch={onNewSearch}
            onNewMoodboard={onNewMoodboard}
            onNewTask={onNewTask}
            statusFilter={statusFilter}
          />
        ))}
        {node.disenosNode && (
          <TreeBranch
            key={node.disenosNode.id}
            node={node.disenosNode}
            depth={depth + 1}
            expanded={expanded}
            toggle={toggle}
            selectedId={selectedId}
            selectedSearchId={selectedSearchId}
            selectedMoodboardId={selectedMoodboardId}
            onSelectTask={onSelectTask}
            onSelectSearch={onSelectSearch}
            onSelectMoodboard={onSelectMoodboard}
            onNewSearch={onNewSearch}
            onNewMoodboard={onNewMoodboard}
            onNewTask={onNewTask}
            statusFilter={statusFilter}
          />
        )}
        {node.searchsNode && (
          <TreeBranch
            key={node.searchsNode.id}
            node={node.searchsNode}
            depth={depth + 1}
            expanded={expanded}
            toggle={toggle}
            selectedId={selectedId}
            selectedSearchId={selectedSearchId}
            selectedMoodboardId={selectedMoodboardId}
            onSelectTask={onSelectTask}
            onSelectSearch={onSelectSearch}
            onSelectMoodboard={onSelectMoodboard}
            onNewSearch={onNewSearch}
            onNewMoodboard={onNewMoodboard}
            onNewTask={onNewTask}
            statusFilter={statusFilter}
          />
        )}
        {node.moodboardsNode && (
          <TreeBranch
            key={node.moodboardsNode.id}
            node={node.moodboardsNode}
            depth={depth + 1}
            expanded={expanded}
            toggle={toggle}
            selectedId={selectedId}
            selectedSearchId={selectedSearchId}
            selectedMoodboardId={selectedMoodboardId}
            onSelectTask={onSelectTask}
            onSelectSearch={onSelectSearch}
            onSelectMoodboard={onSelectMoodboard}
            onNewSearch={onNewSearch}
            onNewMoodboard={onNewMoodboard}
            onNewTask={onNewTask}
            statusFilter={statusFilter}
          />
        )}
        {isSearchs &&
          searches.map((search) => {
            const selected = Number(selectedSearchId) === Number(search.id);
            return (
              <Box
                key={`s-${search.id}`}
                onClick={() => onSelectSearch?.(search.id)}
                sx={leafSx(selected, accent)}
              >
                <ManageSearchOutlinedIcon
                  sx={{
                    fontSize: 17,
                    color: selected ? accent : "text.disabled",
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="body2"
                  fontWeight={selected ? 700 : 600}
                  noWrap
                  sx={{ flex: 1, minWidth: 0, lineHeight: 1.25 }}
                >
                  {search.Titulo}
                </Typography>
                <Chip
                  size="small"
                  label={searchStatusLabel(search.Status)}
                  sx={statusChipSx}
                />
              </Box>
            );
          })}
        {isMoodboards &&
          moodboards.map((mb) => {
            const selected = Number(selectedMoodboardId) === Number(mb.id);
            return (
              <Box
                key={`mb-${mb.id}`}
                onClick={() => onSelectMoodboard?.(mb.id)}
                sx={leafSx(selected, accent)}
              >
                <DashboardCustomizeOutlinedIcon
                  sx={{
                    fontSize: 17,
                    color: selected ? accent : "text.disabled",
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="body2"
                  fontWeight={selected ? 700 : 600}
                  noWrap
                  sx={{ flex: 1, minWidth: 0, lineHeight: 1.25 }}
                >
                  {mb.Titulo}
                </Typography>
                <Chip
                  size="small"
                  label={moodboardStatusLabel(mb.Status)}
                  sx={statusChipSx}
                />
              </Box>
            );
          })}
        {isDisenos &&
          tasks.map((task) => {
            const selected = Number(selectedId) === Number(task.id);
            return (
              <Box
                key={task.id}
                onClick={() => onSelectTask(task.id)}
                sx={leafSx(selected, accent)}
              >
                <AssignmentOutlinedIcon
                  sx={{
                    fontSize: 17,
                    color: selected ? accent : "text.disabled",
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="body2"
                  fontWeight={selected ? 700 : 600}
                  noWrap
                  sx={{ flex: 1, minWidth: 0, lineHeight: 1.25 }}
                >
                  {task.Titulo}
                </Typography>
                <Chip
                  size="small"
                  label={TASK_STATUS_LABELS[task.Status] || task.Status}
                  color={taskStatusColor(task.Status)}
                  sx={statusChipSx}
                />
              </Box>
            );
          })}
      </Collapse>
    </Box>
  );
}

function parseArtifactMeta(artifact) {
  const raw = artifact?.MetaJson;
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

function ArtifactThumb({ artifact, selected, onToggle, showResearchDebug }) {
  const isText =
    artifact.Kind === "text" || parseArtifactMeta(artifact).kind === "research";
  const [src, setSrc] = useState("");
  const [failed, setFailed] = useState(false);
  const [researchOpen, setResearchOpen] = useState(false);

  useEffect(() => {
    if (isText) return undefined;
    let revoked = false;
    let objectUrl = "";
    (async () => {
      try {
        objectUrl = await fetchSkillAgentArtifactBlobUrl(artifact.id);
        if (!revoked) setSrc(objectUrl);
      } catch {
        if (!revoked) {
          setSrc("");
          setFailed(true);
        }
      }
    })();
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [artifact.id, isText]);

  if (isText) {
    if (!showResearchDebug) return null;
    const meta = parseArtifactMeta(artifact);
    const brief = meta.brief || artifact.Title || "";
    const brandCount = Array.isArray(meta.brandsOk) ? meta.brandsOk.length : 0;
    return (
      <Box
        sx={{
          gridColumn: "1 / -1",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          bgcolor: alpha("#00838f", 0.04),
          overflow: "hidden",
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          onClick={() => setResearchOpen((v) => !v)}
          sx={{
            px: 1.25,
            py: 0.75,
            cursor: "pointer",
            userSelect: "none",
            "&:hover": { bgcolor: alpha("#00838f", 0.06) },
          }}
        >
          <Chip
            size="small"
            color="info"
            label="Research"
            sx={{ height: 22 }}
          />
          <Typography variant="subtitle2" fontWeight={800} sx={{ flex: 1 }}>
            {artifact.Title || "Research radar"}
            {brandCount ? (
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                sx={{ ml: 1, fontWeight: 600 }}
              >
                {brandCount} marcas
              </Typography>
            ) : null}
          </Typography>
          <ExpandMoreIcon
            fontSize="small"
            sx={{
              color: "text.secondary",
              transform: researchOpen ? "rotate(180deg)" : "none",
              transition: "transform 0.15s ease",
            }}
          />
        </Stack>
        <Collapse in={researchOpen}>
          <Box sx={{ px: 1.5, pb: 1.5, pt: 0.25 }}>
            {Array.isArray(meta.brandsOk) && meta.brandsOk.length ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.75 }}
              >
                {meta.brandsOk.join(" · ")}
              </Typography>
            ) : null}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: "pre-wrap", lineHeight: 1.45 }}
            >
              {brief}
            </Typography>
          </Box>
        </Collapse>
      </Box>
    );
  }

  return (
    <Box
      onClick={onToggle}
      sx={{
        border: "2px solid",
        borderColor: selected ? "primary.main" : "divider",
        borderRadius: 2,
        overflow: "hidden",
        cursor: "pointer",
        bgcolor: "background.paper",
        position: "relative",
      }}
    >
      <Box
        sx={{
          aspectRatio: "3 / 4",
          bgcolor: "action.hover",
          display: "grid",
          placeItems: "center",
        }}
      >
        {src ? (
          <Box
            component="img"
            src={src}
            alt={artifact.Title || artifact.FileName}
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : failed ? (
          <Typography variant="caption" color="text.secondary" sx={{ p: 1 }}>
            Sin preview
          </Typography>
        ) : (
          <CircularProgress size={28} />
        )}
      </Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ px: 1, py: 0.75 }}
      >
        <Checkbox size="small" checked={selected} sx={{ p: 0.25 }} />
        <Typography variant="caption" fontWeight={700} noWrap sx={{ flex: 1 }}>
          {artifact.Title || artifact.FileName}
        </Typography>
        {artifact.DriveFileId ? (
          <Chip
            size="small"
            label="Drive"
            color="success"
            sx={{ height: 20 }}
          />
        ) : null}
      </Stack>
    </Box>
  );
}

function TaskDetailPanel({
  taskId,
  fichas,
  searches = [],
  onChanged,
  onDeleted,
  showResearchDebug = false,
}) {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [task, setTask] = useState(null);
  const [form, setForm] = useState(null);
  const [artifacts, setArtifacts] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [autoUploadDrive, setAutoUploadDrive] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [pushMsg, setPushMsg] = useState("");
  const [pushing, setPushing] = useState(false);

  const loadArtifacts = useCallback(async () => {
    if (!taskId) return;
    try {
      const data = await listSkillAgentArtifacts(taskId);
      setArtifacts(Array.isArray(data?.artifacts) ? data.artifacts : []);
      if (data?.status && task && data.status !== task.Status) {
        const row = await getSkillAgentTaskById(taskId);
        setTask(row);
        onChanged?.(row);
      }
    } catch {
      /* ignore */
    }
  }, [taskId, task, onChanged]);

  const load = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    setError("");
    setOkMsg("");
    try {
      const row = await getSkillAgentTaskById(taskId);
      setTask(row);
      setAutoUploadDrive(Boolean(row.AutoUploadDrive));
      setRunning(row.Status === "running");
      const c =
        row.ConstraintsJson && typeof row.ConstraintsJson === "object"
          ? row.ConstraintsJson
          : {};
      setForm({
        Titulo: row.Titulo || "",
        Brief: row.Brief || "",
        Temporada: row.Temporada || "",
        TipoTarea: row.TipoTarea || "otro",
        SkillFichaId: row.SkillFichaId ? String(row.SkillFichaId) : "",
        ConstraintsJson: {
          tipología: c.tipología || c.tipologia || "",
          coste: c.coste || "",
          mercado: c.mercado || "",
          refsAncla: c.refsAncla || "",
          numPropuestas:
            c.numPropuestas != null ? String(c.numPropuestas) : "6",
          searchId: c.searchId != null ? String(c.searchId) : "",
        },
      });
      try {
        const data = await listSkillAgentArtifacts(taskId);
        setArtifacts(Array.isArray(data?.artifacts) ? data.artifacts : []);
      } catch {
        setArtifacts([]);
      }
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo cargar.");
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!taskId || !running) return undefined;
    const t = setInterval(async () => {
      try {
        const row = await getSkillAgentTaskById(taskId);
        setTask(row);
        onChanged?.(row);
        if (row.Status !== "running") {
          setRunning(false);
          const data = await listSkillAgentArtifacts(taskId);
          setArtifacts(Array.isArray(data?.artifacts) ? data.artifacts : []);
          if (row.Status === "needs_review") {
            setOkMsg(row.ResultSummary || "Propuestas listas para revisar.");
          } else if (row.Status === "failed") {
            setError(row.ResultSummary || "Falló la generación.");
          }
        }
      } catch {
        /* keep polling */
      }
    }, 4000);
    return () => clearInterval(t);
  }, [taskId, running, onChanged]);

  const readOnly = task && ["running", "done"].includes(task.Status);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const constraints = {
        ...form.ConstraintsJson,
        numPropuestas: Number(form.ConstraintsJson.numPropuestas) || 6,
        searchId: form.ConstraintsJson.searchId
          ? Number(form.ConstraintsJson.searchId)
          : undefined,
      };
      if (!constraints.searchId) delete constraints.searchId;
      const updated = await updateSkillAgentTask(taskId, {
        Titulo: form.Titulo,
        Brief: form.Brief,
        Temporada: form.Temporada,
        TipoTarea: form.TipoTarea,
        SkillFichaId: form.SkillFichaId ? Number(form.SkillFichaId) : undefined,
        ConstraintsJson: constraints,
      });
      setTask(updated);
      setOkMsg(
        updated?.PromptSnapshot
          ? "Guardado. Prompt técnico actualizado."
          : "Guardado.",
      );
      onChanged?.(updated);
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo guardar.");
    } finally {
      setSaving(false);
    }
  };

  const handleRun = async () => {
    setSaving(true);
    setError("");
    setOkMsg("");
    try {
      await handleSaveInner();
      const res = await runSkillAgentTask(taskId, {
        autoUploadDrive,
        customerFolderName: taskClientLabel(task),
      });
      setTask(res.task);
      setRunning(true);
      setSelectedIds(new Set());
      setOkMsg(
        res.message ||
          "Generación iniciada. En 1–4 min verás las propuestas aquí.",
      );
      onChanged?.(res.task);
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo lanzar.");
      setRunning(false);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveInner = async () => {
    const constraints = {
      ...form.ConstraintsJson,
      numPropuestas: Number(form.ConstraintsJson.numPropuestas) || 6,
      searchId: form.ConstraintsJson.searchId
        ? Number(form.ConstraintsJson.searchId)
        : undefined,
    };
    if (!constraints.searchId) delete constraints.searchId;
    const updated = await updateSkillAgentTask(taskId, {
      Titulo: form.Titulo,
      Brief: form.Brief,
      Temporada: form.Temporada,
      TipoTarea: form.TipoTarea,
      SkillFichaId: form.SkillFichaId ? Number(form.SkillFichaId) : undefined,
      ConstraintsJson: constraints,
    });
    setTask(updated);
    return updated;
  };

  const handleDispatch = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await dispatchSkillAgentTask(taskId, {
        promptOnly: true,
        customerFolderName: taskClientLabel(task),
      });
      setTask(res.task);
      setOkMsg(
        res.task?.DriveFolderUrl
          ? "Carpeta Drive lista (modo avanzado). Usa «Lanzar propuestas» para generar imágenes."
          : `Drive falló: ${
              res.driveNote ||
              res.driveStatus?.initError ||
              "revisa secrets / .env"
            }`,
      );
      onChanged?.(res.task);
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo preparar.");
    } finally {
      setSaving(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handlePushDrive = async (all) => {
    setPushing(true);
    setSaving(true);
    setPushMsg("");
    setError("");
    setOkMsg("");
    try {
      if (!all && !selectedIds.size) {
        setPushMsg("error:Marca al menos una propuesta (clic en la imagen).");
        setError("Marca al menos una propuesta (clic en la imagen).");
        return;
      }
      setPushMsg("info:Subiendo a Drive…");
      const body = all
        ? { all: true, customerFolderName: taskClientLabel(task) }
        : {
            artifactIds: [...selectedIds],
            customerFolderName: taskClientLabel(task),
          };
      const res = await pushSkillAgentArtifactsToDrive(taskId, body);
      setArtifacts(Array.isArray(res.artifacts) ? res.artifacts : artifacts);
      if (res.task) {
        setTask(res.task);
        onChanged?.(res.task);
      }
      const n = res.uploaded?.length || 0;
      const msg = all
        ? `${n} propuestas subidas a Drive.`
        : `${n} propuestas guardadas en Drive.`;
      setOkMsg(msg);
      setPushMsg(`ok:${msg}`);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "No se pudo subir a Drive.";
      setError(msg);
      setPushMsg(`error:${msg}`);
    } finally {
      setPushing(false);
      setSaving(false);
    }
  };

  const handleDone = async () => {
    setSaving(true);
    try {
      const updated = await markSkillAgentTaskDone(taskId);
      setTask(updated);
      setOkMsg("Marcada como hecha.");
      onChanged?.(updated);
    } catch (err) {
      setError(err?.response?.data?.error || "Error.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("¿Cancelar esta tarea?")) return;
    setSaving(true);
    try {
      const updated = await cancelSkillAgentTask(taskId);
      setTask(updated);
      setRunning(false);
      onChanged?.(updated);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("¿Eliminar esta tarea?")) return;
    try {
      await deleteSkillAgentTask(taskId);
      onDeleted?.(taskId);
    } catch {
      setError("No se pudo eliminar.");
    }
  };

  const copyPrompt = async () => {
    if (!task?.PromptSnapshot) return;
    try {
      await navigator.clipboard.writeText(task.PromptSnapshot);
      setOkMsg("Prompt copiado.");
    } catch {
      setError("No se pudo copiar.");
    }
  };

  if (loading || !form) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  const canLaunch =
    task.Status !== "done" &&
    task.Status !== "cancelled" &&
    task.Status !== "running";

  const imageArtifacts = artifacts.filter(
    (a) => a.Kind !== "text" && parseArtifactMeta(a).kind !== "research",
  );

  return (
    <Stack spacing={2} sx={{ p: { xs: 1.5, md: 2.5 } }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ sm: "flex-start" }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            fontWeight={700}
          >
            Encargo #{task.id}
          </Typography>
          <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.25 }}>
            {task.Titulo}
          </Typography>
          <Stack
            direction="row"
            spacing={0.75}
            sx={{ mt: 1 }}
            flexWrap="wrap"
            useFlexGap
          >
            <Chip
              size="small"
              icon={<CalendarMonthOutlinedIcon />}
              label={task.Temporada || "Sin temporada"}
              variant="outlined"
            />
            <Chip
              size="small"
              icon={<BusinessOutlinedIcon />}
              label={taskClientLabel(task)}
              variant="outlined"
            />
            <Chip
              size="small"
              label={TASK_STATUS_LABELS[task.Status] || task.Status}
              color={taskStatusColor(task.Status)}
            />
          </Stack>
        </Box>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Eliminar">
            <IconButton color="error" onClick={handleDelete} size="small">
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {okMsg && (
        <Alert severity="success" onClose={() => setOkMsg("")}>
          {okMsg}
        </Alert>
      )}
      {task.ResultSummary && task.Status === "failed" && (
        <Alert severity="error">{task.ResultSummary}</Alert>
      )}

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 3,
          bgcolor: alpha(theme.palette.background.paper, 0.85),
        }}
      >
        <Stack spacing={1.5}>
          <TextField
            label="Título"
            size="small"
            value={form.Titulo}
            disabled={readOnly}
            onChange={(e) => setForm((p) => ({ ...p, Titulo: e.target.value }))}
            sx={pageFieldSx}
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <FormControl
              size="small"
              sx={{ flex: 1, ...pageFieldSx }}
              disabled={readOnly}
            >
              <InputLabel>Cliente / ficha</InputLabel>
              <Select
                label="Cliente / ficha"
                value={form.SkillFichaId}
                onChange={(e) =>
                  setForm((p) => ({ ...p, SkillFichaId: e.target.value }))
                }
              >
                <MenuItem value="">—</MenuItem>
                {fichas.map((f) => (
                  <MenuItem key={f.id} value={String(f.id)}>
                    {f.Titulo || f.SkillSlug}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{ minWidth: 140, ...pageFieldSx }}
              disabled={readOnly}
            >
              <InputLabel>Tipo</InputLabel>
              <Select
                label="Tipo"
                value={form.TipoTarea}
                onChange={(e) =>
                  setForm((p) => ({ ...p, TipoTarea: e.target.value }))
                }
              >
                {Object.entries(TASK_TYPE_LABELS).map(([k, label]) => (
                  <MenuItem key={k} value={k}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Temporada"
              size="small"
              value={form.Temporada}
              disabled={readOnly}
              onChange={(e) =>
                setForm((p) => ({ ...p, Temporada: e.target.value }))
              }
              sx={{ minWidth: 110, ...pageFieldSx }}
            />
          </Stack>
          <TextField
            label="Brief"
            multiline
            minRows={3}
            value={form.Brief}
            disabled={readOnly}
            onChange={(e) => setForm((p) => ({ ...p, Brief: e.target.value }))}
            sx={pageFieldSx}
          />
          <Typography variant="caption" fontWeight={700} color="text.secondary">
            CONSTRAINTS
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
            {["tipología", "coste", "mercado", "refsAncla"].map((key) => (
              <TextField
                key={key}
                label={key}
                size="small"
                value={form.ConstraintsJson[key] || ""}
                disabled={readOnly}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    ConstraintsJson: {
                      ...p.ConstraintsJson,
                      [key]: e.target.value,
                    },
                  }))
                }
                sx={{ flex: 1, ...pageFieldSx }}
              />
            ))}
            <TextField
              label="Nº propuestas"
              size="small"
              type="number"
              inputProps={{ min: 1, max: 8 }}
              value={form.ConstraintsJson.numPropuestas || "6"}
              disabled={readOnly}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  ConstraintsJson: {
                    ...p.ConstraintsJson,
                    numPropuestas: e.target.value,
                  },
                }))
              }
              sx={{ width: 120, ...pageFieldSx }}
            />
            <FormControl size="small" sx={{ minWidth: 200, ...pageFieldSx }}>
              <InputLabel>Usar SEARCH</InputLabel>
              <Select
                label="Usar SEARCH"
                value={form.ConstraintsJson.searchId || ""}
                disabled={readOnly}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    ConstraintsJson: {
                      ...p.ConstraintsJson,
                      searchId: e.target.value,
                    },
                  }))
                }
              >
                <MenuItem value="">Radar al lanzar</MenuItem>
                {(searches || [])
                  .filter((s) => s.Status === "done")
                  .map((s) => (
                    <MenuItem key={s.id} value={String(s.id)}>
                      {s.Titulo}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Stack>

          <FormControlLabel
            control={
              <Checkbox
                checked={autoUploadDrive}
                disabled={readOnly}
                onChange={(e) => setAutoUploadDrive(e.target.checked)}
              />
            }
            label="Subir todo al terminar (pruebas rápidas)"
          />

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {!readOnly && (
              <Button
                variant="outlined"
                startIcon={<SaveOutlinedIcon />}
                disabled={saving}
                onClick={handleSave}
              >
                Guardar
              </Button>
            )}
            {canLaunch && (
              <Button
                variant="contained"
                startIcon={
                  saving || running ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <PlayArrowOutlinedIcon />
                  )
                }
                sx={pagePrimaryButtonSx}
                disabled={saving || running}
                onClick={handleRun}
              >
                Lanzar propuestas
              </Button>
            )}
            {task.Status === "running" && (
              <Chip
                icon={<CircularProgress size={14} />}
                label="Generando… (no cierres)"
                color="info"
              />
            )}
            {task.Status !== "done" && task.Status !== "cancelled" && (
              <Button
                color="success"
                variant="contained"
                startIcon={<CheckCircleOutlineIcon />}
                disabled={saving || task.Status === "running"}
                onClick={handleDone}
              >
                Hecha
              </Button>
            )}
            {task.Status !== "cancelled" && task.Status !== "done" && (
              <Button color="inherit" disabled={saving} onClick={handleCancel}>
                Cancelar
              </Button>
            )}
            {task.Status === "done" || task.Status === "cancelled" ? (
              <Button
                variant="outlined"
                disabled={saving}
                onClick={async () => {
                  setSaving(true);
                  try {
                    const updated = await updateSkillAgentTask(taskId, {
                      Status: "draft",
                    });
                    setTask(updated);
                    setRunning(false);
                    setOkMsg(
                      "Reabierta en borrador. Ya puedes lanzar de nuevo.",
                    );
                    onChanged?.(updated);
                  } catch (err) {
                    setError(
                      err?.response?.data?.error || "No se pudo reabrir.",
                    );
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                Reabrir
              </Button>
            ) : null}
            {task.DriveFolderUrl && (
              <Button
                startIcon={<OpenInNewOutlinedIcon />}
                href={task.DriveFolderUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Abrir carpeta Drive
              </Button>
            )}
            <Button
              size="small"
              variant="text"
              disabled={saving || task.Status === "running"}
              onClick={handleDispatch}
            >
              Solo preparar carpeta
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 3,
          bgcolor: alpha("#1b5e20", 0.03),
          borderColor: alpha("#1b5e20", 0.14),
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ sm: "center" }}
          spacing={1}
          sx={{ mb: 1.5 }}
        >
          <Box>
            <Typography variant="subtitle2" fontWeight={800}>
              Resultados
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {imageArtifacts.length
                ? `${imageArtifacts.length} propuestas · marca las que valen y guarda en Drive`
                : "Tras lanzar, las previews aparecen aquí"}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              size="small"
              variant="outlined"
              startIcon={
                pushing ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <CloudUploadOutlinedIcon />
                )
              }
              disabled={pushing || saving || !imageArtifacts.length}
              onClick={() => handlePushDrive(false)}
            >
              Guardar seleccionadas
              {selectedIds.size ? ` (${selectedIds.size})` : ""}
            </Button>
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={
                pushing ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <CloudUploadOutlinedIcon />
                )
              }
              disabled={pushing || saving || !imageArtifacts.length}
              onClick={() => handlePushDrive(true)}
            >
              Guardar todas
            </Button>
            <Button
              size="small"
              onClick={loadArtifacts}
              disabled={saving || pushing}
            >
              Actualizar
            </Button>
          </Stack>
        </Stack>

        {pushMsg ? (
          <Alert
            severity={
              pushMsg.startsWith("ok:")
                ? "success"
                : pushMsg.startsWith("info:")
                  ? "info"
                  : "error"
            }
            sx={{ mb: 1.5 }}
            onClose={() => setPushMsg("")}
          >
            {pushMsg.replace(/^(ok|error|info):/, "")}
          </Alert>
        ) : null}

        {task.Status === "running" && !imageArtifacts.length ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Generando propuestas fotorealistas…
            </Typography>
          </Box>
        ) : imageArtifacts.length ||
          (showResearchDebug && artifacts.some((a) => a.Kind === "text")) ? (
          <Box
            sx={{
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: {
                xs: "1fr 1fr",
                sm: "repeat(3, 1fr)",
                md: "repeat(4, 1fr)",
              },
            }}
          >
            {artifacts.map((a) => (
              <ArtifactThumb
                key={a.id}
                artifact={a}
                selected={selectedIds.has(a.id)}
                onToggle={() => toggleSelect(a.id)}
                showResearchDebug={showResearchDebug}
              />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Sin propuestas aún. Guarda el brief y pulsa <b>Lanzar propuestas</b>
            .
          </Typography>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Box
          onClick={() => setPromptOpen((v) => !v)}
          sx={{
            px: 2,
            py: 1.25,
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: "pointer",
            bgcolor: alpha("#0d47a1", 0.04),
            "&:hover": { bgcolor: alpha("#0d47a1", 0.08) },
          }}
        >
          <ExpandMoreIcon
            sx={{
              transform: promptOpen ? "rotate(0deg)" : "rotate(-90deg)",
              transition: "transform 0.15s",
              fontSize: 20,
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              Ver detalle técnico
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Prompt generado
              {task.SkillVersionUsed != null
                ? ` · skill v${task.SkillVersionUsed}`
                : ""}
              {task.DispatchMode ? ` · ${task.DispatchMode}` : ""}
            </Typography>
          </Box>
          {task.PromptSnapshot && (
            <Button
              size="small"
              startIcon={<ContentCopyOutlinedIcon />}
              onClick={(e) => {
                e.stopPropagation();
                copyPrompt();
              }}
            >
              Copiar
            </Button>
          )}
        </Box>
        <Collapse in={promptOpen}>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              borderTop: "1px solid",
              borderColor: "divider",
              bgcolor: alpha("#000", 0.03),
              fontSize: "0.75rem",
              whiteSpace: "pre-wrap",
              maxHeight: 280,
              overflow: "auto",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            }}
          >
            {task.PromptSnapshot ||
              "Se genera al lanzar o al preparar la carpeta."}
          </Box>
        </Collapse>
      </Paper>
    </Stack>
  );
}

export default function SkillAgentTasksPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { contentMargin } = useContext(SidebarContext);

  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [sessionUser, setSessionUser] = useState(null);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [searches, setSearches] = useState([]);
  const [moodboards, setMoodboards] = useState([]);
  const [searchBrands, setSearchBrands] = useState([]);
  const [fichas, setFichas] = useState([]);
  const [meta, setMeta] = useState(null);
  const [groupBy, setGroupBy] = useState("season");
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const [selectedId, setSelectedId] = useState(
    () => searchParams.get("id") || "",
  );
  const [selectedSearchId, setSelectedSearchId] = useState(
    () => searchParams.get("search") || "",
  );
  const [selectedMoodboardId, setSelectedMoodboardId] = useState(
    () => searchParams.get("moodboard") || "",
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [createSearchOpen, setCreateSearchOpen] = useState(false);
  const [createMoodboardOpen, setCreateMoodboardOpen] = useState(false);
  const [searchContext, setSearchContext] = useState(null);
  const [moodboardContext, setMoodboardContext] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [creatingSearch, setCreatingSearch] = useState(false);
  const [creatingMoodboard, setCreatingMoodboard] = useState(false);
  const [moodboardFormError, setMoodboardFormError] = useState("");
  const [newTask, setNewTask] = useState({
    SkillFichaId: "",
    Titulo: "",
    Temporada: "",
    TipoTarea: "propuestas",
    Brief: "",
    searchId: "",
  });
  const [newSearch, setNewSearch] = useState({
    Titulo: "",
    Temporada: "",
    Tipologia: "chaqueta Urban",
    CustomerId: "",
    CustomerName: "",
    brandIds: [],
    urlSelection: {},
    customUrls: "",
    maxImagesPerBrand: "3",
  });
  const [newMoodboard, setNewMoodboard] = useState({
    Titulo: "",
    CategoryLabel: "Aviator - JKT",
    Temporada: "",
    CustomerId: "",
    CustomerName: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }
      const decoded = jwtDecode(token);
      const user = await getUserById(decoded.userId);
      setSessionUser(user || null);
      if (!user?.Permiso_TareasAgentes) {
        setForbidden(true);
        return;
      }
      const [
        tasks,
        fichasData,
        metaData,
        searchesData,
        searchesMeta,
        moodboardsData,
      ] = await Promise.all([
        listSkillAgentTasks({}),
        listSkillFichas({}),
        getSkillAgentTasksMeta(),
        listSkillAgentSearches({}).catch(() => []),
        getSkillAgentSearchesMeta().catch(() => null),
        listSkillAgentMoodboards({}).catch(() => []),
      ]);
      const list = Array.isArray(tasks) ? tasks : [];
      const searchList = Array.isArray(searchesData) ? searchesData : [];
      const moodboardList = Array.isArray(moodboardsData) ? moodboardsData : [];
      setRows(list);
      setSearches(searchList);
      setMoodboards(moodboardList);
      setFichas(Array.isArray(fichasData) ? fichasData : []);
      setMeta(metaData || null);
      setSearchBrands(
        (Array.isArray(searchesMeta?.brands) ? searchesMeta.brands : [])
          .slice()
          .sort((a, b) =>
            String(a.name || a.id).localeCompare(String(b.name || b.id), "es", {
              sensitivity: "base",
            }),
          ),
      );

      setExpanded((prev) => {
        const treeNow = buildAgentTasksTree(
          list,
          "season",
          searchList,
          moodboardList,
        );
        const next = new Set(prev);
        treeNow.forEach((n1) => {
          next.add(n1.id);
          n1.children?.forEach((n2) => {
            next.add(n2.id);
            if (n2.disenosNode) next.add(n2.disenosNode.id);
            if (n2.searchsNode) next.add(n2.searchsNode.id);
            if (n2.moodboardsNode) next.add(n2.moodboardsNode.id);
          });
        });
        return next;
      });
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 403) setForbidden(true);
      else setError("No se pudieron cargar las tareas.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  // Sync selection from URL
  useEffect(() => {
    if (loading || forbidden) return;
    const urlId = searchParams.get("id");
    const urlSearch = searchParams.get("search");
    const urlMoodboard = searchParams.get("moodboard");
    if (urlMoodboard) {
      if (String(selectedMoodboardId) !== String(urlMoodboard)) {
        setSelectedMoodboardId(String(urlMoodboard));
        setSelectedSearchId("");
        setSelectedId("");
      }
      return;
    }
    if (urlSearch) {
      if (String(selectedSearchId) !== String(urlSearch)) {
        setSelectedSearchId(String(urlSearch));
        setSelectedMoodboardId("");
        setSelectedId("");
      }
      return;
    }
    if (urlId) {
      if (String(selectedId) !== String(urlId)) {
        setSelectedId(String(urlId));
        setSelectedSearchId("");
        setSelectedMoodboardId("");
      }
      return;
    }
    if (
      !selectedId &&
      !selectedSearchId &&
      !selectedMoodboardId &&
      rows.some((t) => Number(t.id) === 2)
    ) {
      setSelectedId("2");
      setSearchParams({ id: "2" }, { replace: true });
    }
  }, [
    loading,
    forbidden,
    rows,
    searchParams,
    selectedId,
    selectedSearchId,
    selectedMoodboardId,
    setSearchParams,
  ]);

  const filteredRows = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return rows;
    return rows.filter((r) => {
      const blob = [r.Titulo, r.Brief, r.SkillSlug, r.Temporada, r.CustomerName]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return blob.includes(qq);
    });
  }, [rows, q]);

  const filteredSearches = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return searches;
    return searches.filter((r) => {
      const blob = [r.Titulo, r.Brief, r.Temporada, r.CustomerName, r.Tipologia]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return blob.includes(qq);
    });
  }, [searches, q]);

  const filteredMoodboards = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return moodboards;
    return moodboards.filter((r) => {
      const blob = [r.Titulo, r.CategoryLabel, r.Temporada, r.CustomerName]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return blob.includes(qq);
    });
  }, [moodboards, q]);

  const tree = useMemo(
    () =>
      buildAgentTasksTree(
        filteredRows,
        groupBy,
        filteredSearches,
        filteredMoodboards,
      ),
    [filteredRows, groupBy, filteredSearches, filteredMoodboards],
  );

  const searchesForNewTask = useMemo(() => {
    const ficha = fichas.find(
      (f) => String(f.id) === String(newTask.SkillFichaId),
    );
    const customerId = ficha?.CustomerId;
    const temp = String(newTask.Temporada || "").trim();
    return searches.filter((s) => {
      if (s.Status !== "done") return false;
      if (
        customerId &&
        s.CustomerId &&
        Number(s.CustomerId) !== Number(customerId)
      ) {
        return false;
      }
      if (temp && String(s.Temporada || "").trim() !== temp) return false;
      return true;
    });
  }, [searches, fichas, newTask.SkillFichaId, newTask.Temporada]);

  const driveOk = Boolean(meta?.drive?.configured);
  const sdkOk = Boolean(meta?.cursorSdkConfigured);

  const toggleNode = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectTask = (id) => {
    setSelectedId(String(id));
    setSelectedSearchId("");
    setSelectedMoodboardId("");
    setSearchParams({ id: String(id) });
  };

  const handleSelectSearch = (id) => {
    setSelectedSearchId(String(id));
    setSelectedMoodboardId("");
    setSelectedId("");
    setSearchParams({ search: String(id) });
  };

  const handleSelectMoodboard = (id) => {
    setSelectedMoodboardId(String(id));
    setSelectedSearchId("");
    setSelectedId("");
    setSearchParams({ moodboard: String(id) });
  };

  const openNewTaskDialog = (ctxNode) => {
    const rawSeason = String(ctxNode?.Temporada || "").trim();
    const season = rawSeason && rawSeason !== "Sin temporada" ? rawSeason : "";
    const clientName = String(
      ctxNode?.CustomerName || ctxNode?.clientLabel || "",
    ).trim();
    let fichaId = "";
    if (ctxNode?.CustomerId) {
      const match = fichas.find(
        (f) => Number(f.CustomerId) === Number(ctxNode.CustomerId),
      );
      if (match) fichaId = String(match.id);
    }
    if (!fichaId && clientName) {
      const match = fichas.find(
        (f) =>
          f.Titulo === clientName ||
          f.SkillSlug === clientName ||
          (f.Titulo && clientName.includes(String(f.Titulo))) ||
          (f.SkillSlug && clientName.includes(String(f.SkillSlug))),
      );
      if (match) fichaId = String(match.id);
    }
    setNewTask({
      SkillFichaId: fichaId,
      Titulo: "",
      Temporada: season,
      TipoTarea: "propuestas",
      Brief: "",
      searchId: "",
    });
    setCreateOpen(true);
  };

  const openNewSearchDialog = (ctxNode) => {
    const rawSeason = String(ctxNode?.Temporada || "").trim();
    const season = rawSeason && rawSeason !== "Sin temporada" ? rawSeason : "";
    const clientName = String(
      ctxNode?.CustomerName || ctxNode?.clientLabel || "",
    ).trim();
    let customerId = ctxNode?.CustomerId ? String(ctxNode.CustomerId) : "";
    if (!customerId && clientName) {
      const match = fichas.find(
        (f) =>
          f.Titulo === clientName ||
          f.SkillSlug === clientName ||
          (f.Titulo && clientName.includes(String(f.Titulo))) ||
          (f.SkillSlug && clientName.includes(String(f.SkillSlug))),
      );
      if (match?.CustomerId) customerId = String(match.CustomerId);
    }
    setSearchContext({
      ...(ctxNode || {}),
      CustomerName: clientName || null,
      Temporada: season || null,
      CustomerId: customerId ? Number(customerId) : null,
    });
    setNewSearch({
      Titulo: "",
      Temporada: season,
      Tipologia: "chaqueta Urban",
      CustomerId: customerId,
      CustomerName: clientName,
      brandIds: searchBrands.map((b) => b.id),
      urlSelection: buildDefaultUrlSelection(
        searchBrands,
        searchBrands.map((b) => b.id),
      ),
      customUrls: "",
      maxImagesPerBrand: "3",
    });
    setCreateSearchOpen(true);
  };

  const handleCreateSearch = async () => {
    if (!String(newSearch.Titulo || "").trim()) return;
    if (!newSearch.CustomerId && !newSearch.CustomerName) {
      setError("Abre Nuevo search desde la rama SEARCHS de un cliente.");
      return;
    }
    setCreatingSearch(true);
    setError("");
    try {
      const customUrls = String(newSearch.customUrls || "")
        .split(/[\n,]+/)
        .map((u) => u.trim())
        .filter(Boolean);
      const researchBrands = Array.isArray(newSearch.brandIds)
        ? newSearch.brandIds
        : [];
      if (!researchBrands.length && !customUrls.length) {
        setError("Marca al menos una marca GU o añade una URL.");
        setCreatingSearch(false);
        return;
      }
      const created = await createSkillAgentSearch({
        Titulo: newSearch.Titulo.trim(),
        Temporada: newSearch.Temporada || null,
        Tipologia: newSearch.Tipologia || null,
        CustomerId: newSearch.CustomerId ? Number(newSearch.CustomerId) : null,
        ConstraintsJson: {
          tipología: newSearch.Tipologia || "",
          researchBrands,
          brandUrlSelection: newSearch.urlSelection || {},
          customUrls,
          maxImagesPerBrand: Number(newSearch.maxImagesPerBrand) || 3,
          customerName: newSearch.CustomerName || null,
          preferNewest: true,
          maxAgeDays: 60,
          skipKnown: true,
        },
      });
      setCreateSearchOpen(false);
      await load();
      if (created?.id) handleSelectSearch(created.id);
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo crear el search.");
    } finally {
      setCreatingSearch(false);
    }
  };

  const openNewMoodboardDialog = (ctxNode) => {
    const rawSeason = String(ctxNode?.Temporada || "").trim();
    const season = rawSeason && rawSeason !== "Sin temporada" ? rawSeason : "";
    const clientName = String(
      ctxNode?.CustomerName || ctxNode?.clientLabel || "",
    ).trim();
    let customerId = ctxNode?.CustomerId ? String(ctxNode.CustomerId) : "";
    if (!customerId && clientName) {
      const match = fichas.find(
        (f) =>
          f.Titulo === clientName ||
          f.SkillSlug === clientName ||
          (f.Titulo && clientName.includes(String(f.Titulo))) ||
          (f.SkillSlug && clientName.includes(String(f.SkillSlug))),
      );
      if (match?.CustomerId) customerId = String(match.CustomerId);
    }
    setMoodboardContext({
      CustomerName: clientName || null,
      Temporada: season || null,
      CustomerId: customerId ? Number(customerId) : null,
    });
    setMoodboardFormError("");
    setNewMoodboard({
      Titulo: "Aviator - JKT",
      CategoryLabel: "Aviator - JKT",
      Temporada: season,
      CustomerId: customerId,
      CustomerName: clientName,
    });
    setCreateMoodboardOpen(true);
  };

  const handleCreateMoodboard = async () => {
    const titulo =
      String(newMoodboard.Titulo || "").trim() ||
      String(newMoodboard.CategoryLabel || "").trim();
    if (!titulo) {
      setMoodboardFormError("Pon un título (o una etiqueta).");
      return;
    }
    if (!newMoodboard.CustomerId && !newMoodboard.CustomerName) {
      setMoodboardFormError(
        "Abre Nuevo moodboard desde la rama MOODBOARDS de un cliente.",
      );
      return;
    }
    setCreatingMoodboard(true);
    setMoodboardFormError("");
    setError("");
    try {
      const created = await createSkillAgentMoodboard({
        Titulo: titulo,
        CategoryLabel:
          String(newMoodboard.CategoryLabel || "").trim() || titulo,
        Temporada: newMoodboard.Temporada || null,
        CustomerId: newMoodboard.CustomerId
          ? Number(newMoodboard.CustomerId)
          : null,
      });
      setCreateMoodboardOpen(false);
      await load();
      if (created?.id) handleSelectMoodboard(created.id);
    } catch (err) {
      const msg =
        err?.response?.data?.error || "No se pudo crear el moodboard.";
      setMoodboardFormError(msg);
      setError(msg);
    } finally {
      setCreatingMoodboard(false);
    }
  };

  const handleCreate = async () => {
    if (!newTask.Titulo.trim()) {
      setError("Pon un título.");
      return;
    }
    setCreating(true);
    setError("");
    try {
      const created = await createSkillAgentTask({
        SkillFichaId: newTask.SkillFichaId
          ? Number(newTask.SkillFichaId)
          : null,
        Titulo: newTask.Titulo.trim(),
        Temporada: newTask.Temporada.trim() || null,
        TipoTarea: newTask.TipoTarea,
        Brief: newTask.Brief,
        ConstraintsJson: newTask.searchId
          ? {
              searchId: Number(newTask.searchId),
              tipología: "",
              numPropuestas: 6,
            }
          : { numPropuestas: 6 },
      });
      setCreateOpen(false);
      setNewTask({
        SkillFichaId: "",
        Titulo: "",
        Temporada: "",
        TipoTarea: "propuestas",
        Brief: "",
        searchId: "",
      });
      await load();
      if (created?.id) handleSelectTask(created.id);
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo crear.");
    } finally {
      setCreating(false);
    }
  };

  if (forbidden) {
    return (
      <Box sx={{ ...contentMargin, p: 3 }}>
        <Alert severity="warning">
          No tienes permiso para Tareas Agentes. Pide activar{" "}
          <b>Permiso_TareasAgentes</b>.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        height: "100dvh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: alpha(theme.palette.primary.main, 0.025),
        backgroundImage: `radial-gradient(1000px 400px at 0% -20%, ${alpha(theme.palette.primary.main, 0.09)}, transparent)`,
        ...contentMargin,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 1.5, md: 2.5 },
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: alpha(theme.palette.background.paper, 0.92),
          backdropFilter: "blur(10px)",
          zIndex: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ md: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2.5,
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: "primary.main",
              }}
            >
              <FolderOpenOutlinedIcon />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={800}>
                Tareas de agentes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Árbol Temporada → Cliente → Encargo · igual que en Drive
              </Typography>
            </Box>
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            alignItems="center"
            sx={{
              "& .MuiChip-root": {
                height: 34,
                borderRadius: 2,
                fontWeight: 700,
                "& .MuiChip-icon": { ml: 0.75, fontSize: 18 },
                "& .MuiChip-label": { px: 1.25 },
              },
              "& .MuiButton-root": {
                height: 34,
                minHeight: 34,
                py: 0,
                px: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "none",
              },
            }}
          >
            <Chip
              size="small"
              icon={
                driveOk ? <CloudDoneOutlinedIcon /> : <CloudOffOutlinedIcon />
              }
              label={driveOk ? "Drive listo" : "Drive pendiente"}
              color={driveOk ? "success" : "default"}
              variant={driveOk ? "filled" : "outlined"}
            />
            <Chip
              size="small"
              icon={<SmartToyOutlinedIcon />}
              label={sdkOk ? "SDK automático" : "Modo manual"}
              color={sdkOk ? "info" : "default"}
              variant="outlined"
            />
            <Button
              variant="outlined"
              startIcon={<HelpOutlineOutlinedIcon />}
              onClick={() => setHelpOpen(true)}
              size="small"
            >
              Instrucciones
            </Button>
            <Button
              variant="outlined"
              startIcon={<DescriptionOutlinedIcon />}
              onClick={() => navigate("/skills")}
              size="small"
            >
              Fichas
            </Button>
            <Button
              variant="contained"
              startIcon={<AddOutlinedIcon />}
              onClick={() => setCreateOpen(true)}
              size="small"
              sx={{
                px: 2,
                boxShadow: (t) =>
                  `0 2px 10px ${alpha(t.palette.primary.main, 0.28)}`,
              }}
            >
              Nuevo encargo
            </Button>
          </Stack>
        </Stack>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mx: 2, mt: 1.5, flexShrink: 0 }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      {/* Body: tree + detail */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(300px, 380px) 1fr",
          },
          gap: 0,
        }}
      >
        {/* Tree panel */}
        <Paper
          square
          elevation={0}
          sx={{
            borderRight: { md: "1px solid" },
            borderBottom: { xs: "1px solid", md: "none" },
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
            minHeight: { xs: 280, md: 0 },
            maxHeight: { xs: "42dvh", md: "none" },
            bgcolor: alpha(theme.palette.background.paper, 0.7),
          }}
        >
          <Box
            sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider" }}
          >
            <ToggleButtonGroup
              exclusive
              size="small"
              value={groupBy}
              onChange={(_, v) => v && setGroupBy(v)}
              fullWidth
              sx={{
                mb: 1.25,
                height: 34,
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  fontWeight: 700,
                  py: 0,
                  height: 34,
                  borderRadius: "8px !important",
                },
              }}
            >
              <ToggleButton value="season">Por temporada</ToggleButton>
              <ToggleButton value="client">Por cliente</ToggleButton>
            </ToggleButtonGroup>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                placeholder="Buscar…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  ...pageFieldSx,
                  "& .MuiOutlinedInput-root": { height: 34, borderRadius: 2 },
                }}
              />
              <FormControl
                size="small"
                sx={{
                  minWidth: 118,
                  ...pageFieldSx,
                  "& .MuiOutlinedInput-root": { height: 34, borderRadius: 2 },
                }}
              >
                <InputLabel>Estado</InputLabel>
                <Select
                  label="Estado"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {Object.entries(TASK_STATUS_LABELS).map(([k, label]) => (
                    <MenuItem key={k} value={k}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Box>

          <Box sx={{ flex: 1, overflow: "auto", p: 1 }}>
            {loading ? (
              <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
                <CircularProgress size={28} />
              </Box>
            ) : tree.length === 0 ? (
              <Box sx={{ py: 6, px: 2, textAlign: "center" }}>
                <FolderOpenOutlinedIcon
                  sx={{ fontSize: 40, color: "text.disabled", mb: 1 }}
                />
                <Typography fontWeight={700} color="text.secondary">
                  Sin encargos
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Crea el primero para empezar el árbol.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddOutlinedIcon />}
                  sx={pagePrimaryButtonSx}
                  onClick={() => setCreateOpen(true)}
                >
                  Nuevo encargo
                </Button>
              </Box>
            ) : (
              tree.map((node) => (
                <TreeBranch
                  key={node.id}
                  node={node}
                  depth={0}
                  expanded={expanded}
                  toggle={toggleNode}
                  selectedId={selectedId}
                  selectedSearchId={selectedSearchId}
                  selectedMoodboardId={selectedMoodboardId}
                  onSelectTask={handleSelectTask}
                  onSelectSearch={handleSelectSearch}
                  onSelectMoodboard={handleSelectMoodboard}
                  onNewSearch={openNewSearchDialog}
                  onNewMoodboard={openNewMoodboardDialog}
                  onNewTask={openNewTaskDialog}
                  statusFilter={statusFilter}
                />
              ))
            )}
          </Box>
        </Paper>

        {/* Detail panel */}
        <Box
          sx={{
            minHeight: 0,
            overflow: "auto",
            bgcolor: alpha(theme.palette.background.default, 0.35),
          }}
        >
          {selectedMoodboardId ? (
            <SkillAgentMoodboardDetail
              key={selectedMoodboardId}
              moodboardId={selectedMoodboardId}
              onChanged={() => load()}
              onDeleted={() => {
                setSelectedMoodboardId("");
                setSearchParams({});
                load();
              }}
            />
          ) : selectedSearchId ? (
            <SkillAgentSearchDetail
              key={selectedSearchId}
              searchId={selectedSearchId}
              onChanged={() => load()}
              onDeleted={() => {
                setSelectedSearchId("");
                setSearchParams({});
                load();
              }}
            />
          ) : !selectedId ? (
            <Box
              sx={{
                height: "100%",
                minHeight: 320,
                display: "grid",
                placeItems: "center",
                p: 4,
                textAlign: "center",
              }}
            >
              <Box sx={{ maxWidth: 420 }}>
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: 4,
                    mx: "auto",
                    mb: 2,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: "primary.main",
                  }}
                >
                  <AssignmentOutlinedIcon sx={{ fontSize: 36 }} />
                </Box>
                <Typography variant="h6" fontWeight={800}>
                  Elige un diseño, SEARCH o Moodboard
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 1 }}>
                  Bajo cada cliente/temporada: <b>DISEÑOS</b> (encargos),{" "}
                  <b>SEARCHS</b> (fotos) y <b>MOODBOARDS</b> (collages → PDF).
                </Typography>
              </Box>
            </Box>
          ) : (
            <TaskDetailPanel
              key={`${selectedId}-${
                rows.find((r) => Number(r.id) === Number(selectedId))?.Status ||
                ""
              }`}
              taskId={selectedId}
              fichas={fichas}
              searches={searches}
              showResearchDebug={isSkillsApprover(sessionUser)}
              onChanged={(updated) => {
                setRows((prev) =>
                  prev.map((r) =>
                    Number(r.id) === Number(updated.id)
                      ? { ...r, ...updated }
                      : r,
                  ),
                );
              }}
              onDeleted={(id) => {
                setRows((prev) =>
                  prev.filter((r) => Number(r.id) !== Number(id)),
                );
                setSelectedId("");
                setSearchParams({});
              }}
            />
          )}
        </Box>
      </Box>

      <SkillAgentTasksHelpDialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
      />

      {/* Create task dialog */}
      <Dialog
        open={createOpen}
        onClose={() => !creating && setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={800}>Nuevo encargo</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <FormControl size="small" fullWidth sx={pageFieldSx}>
              <InputLabel>Cliente / ficha</InputLabel>
              <Select
                label="Cliente / ficha"
                value={newTask.SkillFichaId}
                onChange={(e) =>
                  setNewTask((p) => ({ ...p, SkillFichaId: e.target.value }))
                }
              >
                <MenuItem value="">Elegir…</MenuItem>
                {fichas.map((f) => (
                  <MenuItem key={f.id} value={String(f.id)}>
                    {f.Titulo || f.SkillSlug}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={1.5}>
              <FormControl size="small" sx={{ flex: 1, ...pageFieldSx }}>
                <InputLabel>Tipo</InputLabel>
                <Select
                  label="Tipo"
                  value={newTask.TipoTarea}
                  onChange={(e) =>
                    setNewTask((p) => ({ ...p, TipoTarea: e.target.value }))
                  }
                >
                  {Object.entries(TASK_TYPE_LABELS).map(([k, label]) => (
                    <MenuItem key={k} value={k}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Temporada"
                placeholder="SS27"
                value={newTask.Temporada}
                onChange={(e) =>
                  setNewTask((p) => ({ ...p, Temporada: e.target.value }))
                }
                sx={{ width: 120, ...pageFieldSx }}
              />
            </Stack>
            <TextField
              size="small"
              label="Título"
              placeholder="Ej. 3 propuestas bomber"
              value={newTask.Titulo}
              onChange={(e) =>
                setNewTask((p) => ({ ...p, Titulo: e.target.value }))
              }
              fullWidth
              sx={pageFieldSx}
            />
            <TextField
              size="small"
              label="Brief"
              multiline
              minRows={3}
              value={newTask.Brief}
              onChange={(e) =>
                setNewTask((p) => ({ ...p, Brief: e.target.value }))
              }
              fullWidth
              sx={pageFieldSx}
            />
            <FormControl size="small" fullWidth sx={pageFieldSx}>
              <InputLabel>Usar SEARCH (opcional)</InputLabel>
              <Select
                label="Usar SEARCH (opcional)"
                value={newTask.searchId}
                onChange={(e) =>
                  setNewTask((p) => ({ ...p, searchId: e.target.value }))
                }
              >
                <MenuItem value="">Sin SEARCH (radar al lanzar)</MenuItem>
                {searchesForNewTask.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    {s.Titulo}
                    {s.Temporada ? ` · ${s.Temporada}` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            sx={pagePrimaryButtonSx}
            disabled={creating}
            onClick={handleCreate}
          >
            {creating ? "Creando…" : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create SEARCH dialog */}
      <Dialog
        open={createSearchOpen}
        onClose={() => !creatingSearch && setCreateSearchOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={800}>Nuevo SEARCH</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Box
              sx={{
                px: 1.5,
                py: 1.25,
                borderRadius: 2,
                bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
              >
                Rama del árbol
              </Typography>
              <Typography variant="body1" fontWeight={800}>
                {[newSearch.CustomerName, newSearch.Temporada]
                  .filter(Boolean)
                  .join(" · ") || "—"}
              </Typography>
            </Box>
            <TextField
              size="small"
              label="Título"
              placeholder="Ej. Radar bombers SS27"
              value={newSearch.Titulo}
              onChange={(e) =>
                setNewSearch((p) => ({ ...p, Titulo: e.target.value }))
              }
              fullWidth
              sx={pageFieldSx}
            />
            <TextField
              size="small"
              label="Tipología / keywords"
              value={newSearch.Tipologia}
              onChange={(e) =>
                setNewSearch((p) => ({ ...p, Tipologia: e.target.value }))
              }
              fullWidth
              multiline
              minRows={2}
              helperText="Separa por comas: Jacket, bomber, fleece, overshirt…"
              sx={pageFieldSx}
            />
            <Stack direction="row" spacing={1.5}>
              <TextField
                size="small"
                label="Imgs/marca"
                type="number"
                inputProps={{ min: 1, max: 12 }}
                value={newSearch.maxImagesPerBrand}
                onChange={(e) =>
                  setNewSearch((p) => ({
                    ...p,
                    maxImagesPerBrand: e.target.value,
                  }))
                }
                helperText="Por cada marca"
                sx={{ width: 120, ...pageFieldSx }}
              />
            </Stack>

            <SkillAgentBrandSourcesFields
              brands={searchBrands}
              brandIds={newSearch.brandIds}
              urlSelection={newSearch.urlSelection}
              onBrandIdsChange={(brandIds) =>
                setNewSearch((p) => ({ ...p, brandIds }))
              }
              onUrlSelectionChange={(urlSelection) =>
                setNewSearch((p) => ({ ...p, urlSelection }))
              }
              maxHeight={280}
            />

            <TextField
              size="small"
              label="Webs extra (URLs, una por línea)"
              placeholder="https://ejemplo.com/collections/jackets"
              multiline
              minRows={2}
              value={newSearch.customUrls}
              onChange={(e) =>
                setNewSearch((p) => ({ ...p, customUrls: e.target.value }))
              }
              fullWidth
              sx={pageFieldSx}
              helperText="Opcional. Solo novedades (~60 días). No vuelve a bajar fotos ya guardadas de este cliente/temporada."
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setCreateSearchOpen(false)}
            disabled={creatingSearch}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            sx={pagePrimaryButtonSx}
            disabled={creatingSearch}
            onClick={handleCreateSearch}
          >
            {creatingSearch ? "Creando…" : "Crear SEARCH"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Moodboard dialog */}
      <Dialog
        open={createMoodboardOpen}
        onClose={() => !creatingMoodboard && setCreateMoodboardOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={800}>Nuevo Moodboard</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            {moodboardFormError ? (
              <Alert severity="error" onClose={() => setMoodboardFormError("")}>
                {moodboardFormError}
              </Alert>
            ) : null}
            <Box
              sx={{
                px: 1.5,
                py: 1.25,
                borderRadius: 2,
                bgcolor: (t) => alpha(t.palette.warning.main, 0.08),
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
              >
                Rama del árbol
              </Typography>
              <Typography variant="body1" fontWeight={800}>
                {[newMoodboard.CustomerName, newMoodboard.Temporada]
                  .filter(Boolean)
                  .join(" · ") || "—"}
              </Typography>
            </Box>
            <TextField
              size="small"
              label="Título"
              placeholder="Ej. Aviator board"
              value={newMoodboard.Titulo}
              onChange={(e) =>
                setNewMoodboard((p) => ({ ...p, Titulo: e.target.value }))
              }
              required
              autoFocus
              fullWidth
              sx={pageFieldSx}
            />
            <TextField
              size="small"
              label="Etiqueta barra derecha"
              placeholder="Aviator - JKT"
              value={newMoodboard.CategoryLabel}
              onChange={(e) =>
                setNewMoodboard((p) => ({
                  ...p,
                  CategoryLabel: e.target.value,
                }))
              }
              fullWidth
              sx={pageFieldSx}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setCreateMoodboardOpen(false)}
            disabled={creatingMoodboard}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            sx={pagePrimaryButtonSx}
            disabled={creatingMoodboard}
            onClick={handleCreateMoodboard}
          >
            {creatingMoodboard ? "Creando…" : "Crear Moodboard"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
